import { useMemo } from "react";
import dayjs from "dayjs";
import { Space, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Button } from "@libertify/ui";
import type { BundleResponse } from "@/types";
import StatusTag from "../../../components/StatusTag";
import {
  getBundleStatusVisual,
  resolveUiStatus,
} from "../../helpers/dataExport";
import DownloadAction from "./DownloadAction";
import { useTranslate } from "#src/hooks";

const numericCellStyle = { fontVariantNumeric: "tabular-nums" } as const;

type HistoryTableProps = {
  bundles: BundleResponse[];
  isLoading: boolean;
  onReExport: (bundle: BundleResponse) => void;
};

export const HistoryTable = ({
  bundles,
  isLoading,
  onReExport,
}: HistoryTableProps) => {
  const t = useTranslate();

  const columns = useMemo<ColumnsType<BundleResponse>>(
    () => [
      {
        title: t("media.organizations.dataExport.col.requestedAt"),
        dataIndex: "requested_at",
        key: "requested_at",
        render: (value: string) => (
          <span style={numericCellStyle}>
            {dayjs(value).format("YYYY-MM-DD HH:mm")}
          </span>
        ),
      },
      {
        title: t("media.organizations.dataExport.col.dateRange"),
        key: "range",
        render: (_value, row) => {
          if (!row.date_from && !row.date_to) {
            return (
              <Typography.Text type="secondary">
                {t("media.organizations.dataExport.fullHistory")}
              </Typography.Text>
            );
          }
          return (
            <span style={numericCellStyle}>
              {row.date_from ?? "…"} – {row.date_to ?? "…"}
            </span>
          );
        },
      },
      {
        title: t("media.organizations.dataExport.col.status"),
        key: "status",
        align: "center",
        render: (_value, row) => {
          const uiStatus = resolveUiStatus(row);
          const visual = getBundleStatusVisual(uiStatus);
          return (
            <StatusTag
              icon={visual.icon}
              color={visual.color}
              label={t(visual.i18nKey)}
            />
          );
        },
      },
      {
        title: t("media.organizations.dataExport.col.expiresAt"),
        dataIndex: "expires_at",
        key: "expires_at",
        render: (value: string | null) =>
          value ? (
            <span style={numericCellStyle}>
              {dayjs(value).format("YYYY-MM-DD")}
            </span>
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          ),
      },
      {
        title: t("media.organizations.dataExport.col.action"),
        key: "action",
        align: "center",
        render: (_value, row) => {
          const uiStatus = resolveUiStatus(row);
          if (uiStatus === "Completed") {
            return <DownloadAction bundle={row} />;
          }
          if (uiStatus === "Expired" || uiStatus === "Failed") {
            return (
              <Button variant="outlined" onClick={() => onReExport(row)}>
                {t("media.organizations.dataExport.reExport")}
              </Button>
            );
          }
          return null;
        },
      },
    ],
    [onReExport, t]
  );

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Typography.Title level={5}>
        {t("media.organizations.dataExport.history")}
      </Typography.Title>
      <Table<BundleResponse>
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={bundles}
        pagination={false}
      />
    </Space>
  );
};

export default HistoryTable;
